import React, { ReactElement } from 'react';
import cn from 'classnames';
import ReactSelect, {
  ActionMeta,
  components,
  ControlProps,
  DropdownIndicatorProps,
  IndicatorsContainerProps,
  IndicatorSeparatorProps,
  InputProps,
  MenuListProps,
  MenuPlacement,
  MenuProps,
  OptionProps,
  PlaceholderProps,
  SingleValueProps,
  ValueContainerProps,
} from 'react-select';
import ReactSelectCreatable from 'react-select/creatable';
import styles from './Select.module.scss';
import { CheckIcon, ChevronDownIcon } from '@repo/icons';

export type SelectOption<T> = { label: any; value: T };

interface SelectProps<T> {
  options: Array<SelectOption<T>>;
  className?: string;
  isSearchable?: boolean;
  defaultValue?: SelectOption<T>;
  value?: SelectOption<T>;
  placeholder?: string;
  maxMenuHeight?: number;
  menuPlacement?: MenuPlacement;
  onChange: (selectedOption: SelectOption<T> | null, action: ActionMeta<SelectOption<T>>) => void;
  onMenuOpen?: () => void;
  isDisabled?: boolean;
  // Custom props. Everything above here is standard react-select.
  isInline?: boolean;
  noContainer?: boolean;
  noMenuWrap?: boolean;
  alignMenuRight?: boolean;
  dropdownIndicator?: (props: DropdownIndicatorProps) => ReactElement;
}

/* Customized Select components:
 **  Custom props can be passed to any of these components by adding additional props
 **  to the <ReactSelect/> component. These custom props are available to the children
 **  components as members of the `selectProps` prop, e.g.
 **
 **  const { isInline } = props.selectProps
 */
const Control = ({ className, noContainer, ...props }: ControlProps & { noContainer: boolean }) => (
  <components.Control
    className={cn(
      className,
      'select-control',
      styles.control,
      // props.isFocused && styles.controlFocused,
      props.isFocused && 'select-control-focused',
      noContainer && styles.noContainer,
    )}
    {...props}
  />
);

const IndicatorSeparator = (_props: IndicatorSeparatorProps) => null;

const IndicatorsContainer = ({ ...props }: IndicatorsContainerProps) => (
  <components.IndicatorsContainer
    {...props}
    className={cn(props.className, 'select-indicators-container')}
  />
);

const DropdownIndicator = ({ className, children, ...props }: DropdownIndicatorProps) => (
  <components.DropdownIndicator
    className={cn(className, styles.dropdown, 'select-dropdown-indicator')}
    {...props}
  >
    <ChevronDownIcon color="textSecondary" />
  </components.DropdownIndicator>
);

const ValueContainer = ({ className, ...props }: ValueContainerProps) => (
  <components.ValueContainer
    className={cn(className, 'select-value-container', styles.valueContainer)}
    {...props}
  />
);

const Input = ({ className, ...props }: InputProps) => (
  <components.Input className={cn(className, styles.input)} {...props} />
);

const SingleValue = ({ className, ...props }: SingleValueProps) => (
  <components.SingleValue className={cn(className, styles.singleValue)} {...props} />
);

const Placeholder = ({ className, ...props }: PlaceholderProps) => (
  <components.Placeholder className={cn(className, styles.placeholder)} {...props} />
);

const Menu = ({
  className,
  noWrap,
  alignRight,
  ...props
}: MenuProps & { noWrap: boolean; alignRight: boolean }) => (
  <components.Menu
    className={cn(
      className,
      styles.menu,
      noWrap && styles.noWrap,
      alignRight && styles.alignRight,
      'select-menu',
    )}
    {...props}
  />
);

const MenuList = ({ className, ...props }: MenuListProps) => (
  <components.MenuList className={cn(className, styles.menuList)} {...props} />
);

const Option = ({ children, className, ...props }: OptionProps) => (
  <components.Option
    className={cn(
      className,
      styles.option,
      props.isSelected && styles.optionSelected,
      props.isFocused && styles.optionFocused,
    )}
    {...props}
  >
    {children}
    {props.isSelected ? <CheckIcon customColor="#2686be" strokeWidth={2.4} width={11} /> : <span />}
  </components.Option>
);

export function Select<OptionValueType>({
  options,
  className,
  menuPlacement = 'auto',
  isSearchable = false,
  defaultValue = null,
  value = null,
  placeholder,
  maxMenuHeight,
  onChange,
  onMenuOpen,
  isDisabled = false,
  isInline = false,
  noContainer = false,
  noMenuWrap = false,
  alignMenuRight = false,
  dropdownIndicator,
}: SelectProps<OptionValueType>) {
  return (
    <ReactSelect
      className={cn(
        styles.select,
        className,
        isInline && styles.isInline,
        isDisabled && styles.disabled,
        'select-control',
      )}
      menuPlacement={menuPlacement}
      placeholder={placeholder}
      options={options}
      defaultValue={defaultValue}
      maxMenuHeight={maxMenuHeight}
      onChange={onChange}
      onMenuOpen={onMenuOpen}
      components={{
        Control: (props) => <Control {...props} noContainer={noContainer} />,
        IndicatorSeparator,
        IndicatorsContainer,
        DropdownIndicator: dropdownIndicator || DropdownIndicator,
        Menu: (props) => <Menu {...props} noWrap={noMenuWrap} alignRight={alignMenuRight} />,
        MenuList,
        ValueContainer,
        Input,
        SingleValue,
        Placeholder,
        Option,
      }}
      isSearchable={isSearchable}
      isDisabled={isDisabled}
      isOptionDisabled={(option: { value: any; label: string; disabled?: boolean }) =>
        option?.disabled
      }
      // @ts-ignore: custom selectProps members below here, for consumption by custom components
      isInline={isInline}
      noOptionsMessage={() => '--'}
      {...(value ? { value } : {})}
    />
  );
}

export function SelectOrAddValue<OptionValueType>({
  options,
  className,
  menuPlacement = 'auto',
  defaultValue = null,
  value = null,
  placeholder,
  maxMenuHeight,
  onChange,
  onMenuOpen,
  isDisabled = false,
  isInline = false,
  noContainer = false,
  noMenuWrap = false,
  alignMenuRight = false,
  dropdownIndicator,
}: SelectProps<OptionValueType>) {
  return (
    <ReactSelectCreatable
      className={cn(
        styles.select,
        className,
        isInline && styles.isInline,
        isDisabled && styles.disabled,
      )}
      menuPlacement={menuPlacement}
      placeholder={placeholder}
      options={options}
      defaultValue={defaultValue}
      maxMenuHeight={maxMenuHeight}
      onChange={onChange}
      onMenuOpen={onMenuOpen}
      components={{
        Control: (props) => <Control {...props} noContainer={noContainer} />,
        IndicatorSeparator,
        IndicatorsContainer,
        DropdownIndicator: dropdownIndicator || DropdownIndicator,
        Menu: (props) => <Menu {...props} noWrap={noMenuWrap} alignRight={alignMenuRight} />,
        MenuList,
        ValueContainer,
        Input,
        SingleValue,
        Placeholder,
        Option,
      }}
      isSearchable
      isDisabled={isDisabled}
      isOptionDisabled={(option: { value: any; label: string; disabled?: boolean }) =>
        option?.disabled
      }
      // @ts-ignore: custom selectProps members below here, for consumption by custom components
      isInline={isInline}
      noOptionsMessage={() => '--'}
      {...(value ? { value } : {})}
      formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
      createOptionPosition="last"
    />
  );
}
